import { Location } from "@angular/common";
import { HttpErrorResponse } from "@angular/common/http";
import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from "@angular/core";
import {
  FormBuilder,
  FormControl,
  FormGroup,
  NgSelectOption,
} from "@angular/forms";
import { NgSelectComponent } from "@ng-select/ng-select";
import { AuthService } from "app/auth/auth.service";
import { FaxModalComponent } from "components/fax/fax-modal/fax-modal";
import {
  AlertController,
  App,
  Checkbox,
  Events,
  IonicPage,
  LoadingController,
  ModalController,
  NavParams,
  PopoverController,
  ToastController,
  Toggle,
} from "ionic-angular";
import SortOptions from "models/content-wrapper/sort-options";
import CareTeam from "models/idg/company-team";
import {
  getHistoryDetailSummary,
  HistoryDetailSummary,
} from "models/order-management/historyDetailSummary";
import {
  Action,
  DropdownItem,
  Order,
  OrderErrorResponse,
  OrderResponse,
  OrderSearchDateRange,
  OrderSearchRequest,
  OrderSignatureRequest,
  OrderTypeConfiguration,
} from "models/order-management/models";
import { StatefulOrderResponse } from "models/order-management/statefullOrderResponse";
import { NciIndicator } from "models/patient-chart/nci-indicator";
import CompanyResourceType from "models/resource-management/company-resource-type.enum";
import ProfessionalProvider from "models/resource-management/professional-provider";
import * as moment from "moment-timezone";
import { EditViewPopoverPage } from "navigation/components/edit-view-popover/edit-view-popover";
import {
  getDiscontinuedProviderDisplayName,
  getOrderTypeDisplayName,
  OrderTypeSubtype,
} from "pages/orders-entry-om/order-utils";
import { UtcDateTimeZonePipe } from "pipes/output-formatters/utc-date-timezone.pipe";
import { CareTeamsService } from "providers/care-teams-service/care-teams-service";
import { ConfigurationService } from "providers/configuration/configuration.service";
import { DocManagementDataService } from "providers/doc-management-data-service/doc-management-data-service";
import { NciIndicatorsService } from "providers/nci-indicators/nci-indicators-service";
import { OrderManagementDataService } from "providers/order-management-data-service/order-management-data-service";
import { ReportExecutionService } from "providers/report-execution-service/report-execution.service";
import { ResourceManagementService } from "providers/resource-management/resource-management.service";
import { Observable, Subject, Subscription } from "rxjs";
import { of } from "rxjs/observable/of";
import {
  catchError,
  distinctUntilChanged,
  filter,
  mergeMap,
  switchMap,
} from "rxjs/operators";
import { NavigationService } from "../../../navigation/navigation.service";
import { OrderDataService } from "../../../providers/order-data-service/order-data-service";
import { StateManagementService } from "../../../providers/state-management/state-management.service";
import { BasePage } from "../../base-page";
import { ExternalExportOrderModalComponent } from "../external-export-order-modal/external-export-order-modal";

/**
 * Generated class for the OrderEntryPage page.
 *
 * See https://ionicframework.com/docs/components/#navigation for more info on
 * Ionic pages and navigation.
 */

const DATE_RANGE_COLUMN = "startDate";
const MSG_PRINT_ALL =
  "You are about to download a large file. Do you want to proceed with download?";
const NO_ORDERS_TO_PRINT_FILTERED =
  "There are no orders to print based on the selected filters";
const NO_ORDERS_TO_PRINT_SELECTED = "No orders selected to print";
const NO_ORDERS_TO_FAX =
  "There are no orders to fax based on the selected filters";
const ALL_PATIENT_ORDERS_QUICKSIGHT_DASHBOARD_NAME = "all_patient_orders";

@IonicPage({
  segment: "patient/listOrders",
})
@Component({
  selector: "page-list-orders",
  templateUrl: "list-orders.html",
})
export class ListOrdersPage extends BasePage implements OnInit, OnDestroy {
  @ViewChild("orderTypeDropdown") orderTypeDropdown;
  @ViewChild("signedDropdown") signedDropdown;
  @ViewChild("teamDropdown") teamDropdown;
  @ViewChild("orderingProvider") orderingProvider;
  @ViewChild("hiddenInput") hiddenInput;
  @ViewChild("checkedOrdersDropdown") checkedOrdersDropdown: NgSelectComponent;
  @ViewChild("hospiceCoverageDropdown") hospiceCoverageDropdown;

  totalPages: number = 0;
  currentPage: number = 0;
  pageSize: number = 100;
  from: number = 0;

  searchForm: FormGroup;
  error: boolean = false;
  searchField = new FormControl("");
  professionalProviders: Map<number, ProfessionalProvider> = new Map<
    number,
    ProfessionalProvider
  >();
  orderTypeDropdownItems: Array<DropdownItem> = [];
  spinnerCounter = 0;
  spinner = null;

  printOptions: any[] = [{ name: "Print", value: { key: "print" } }];

  faxOptions: any[] = [{ name: "Fax", value: { key: "fax" } }];

  externalExportOptions: any[] = [
    { name: "External Export", value: { key: "externalExport" } },
  ];

  userDisplayName: string;

  // Filters
  orders: Array<StatefulOrderResponse> = [];

  // TRD-201 / REQ-006 (TRD §4.4): "N" indicator data sourced from
  // GET /nci-indicators?orderId=. Keyed by `Number(order.orderId)` for O(1)
  // template lookup via `getNciIndicator(order)`. Refreshed alongside
  // `this.orders` in loadData(). See order-entry-om.ts for matching pattern.
  nciIndicators: Map<number, NciIndicator> = new Map<number, NciIndicator>();

  tempNciToggle: boolean = false;
  tempNciToggleSubscription: Subscription;

  orderTypeConfigurations: Array<OrderTypeConfiguration> = [];
  fromDate: any = null;
  toDate: any = null;
  orderSearchDetails: OrderSearchRequest = null;
  search: string = null;
  discontinuedOrdersEnabled: boolean = false;
  signedOrdersEnabled: boolean = false;
  signedStatus?: OrderSignatureRequest.SignedStatusEnum = null;
  hospiceCoverage: boolean = null;
  historyDetails: HistoryDetailSummary[] = [];

  providerDropdownItems$: Observable<DropdownItem[]>;
  providerQuery$ = new Subject<string>();
  orderingProviderId: number;

  sortColumn: string = "startDate";
  sortDirection: "asc" | "desc" = "desc";
  orderTemp = null;
  orderTypes: string;

  teamDropdownItems = [];
  team: number;

  selectAllOrdersCheckbox = false;
  checkedOrdersMap: Record<string, StatefulOrderResponse> = {};
  checkedOrders: string[] = [];
  fromDateFlag = false;
  toDateFlag = false;
  type: string = "OM";
  newPatientProfileBaseUrl: string;
  newPatientChartData: any;
  patientProfileUrl: string;

  showQuicksightDashboard = false;
  quicksightDashboardName: string;
  quicksightDashboardParameters: any;

  isOrderRejected: boolean = false;
  isOrderDiscontinued: boolean = false;
  private boundOnSearchIconClick: () => void;
  private searchIconEl: Element | null = null;

  constructor(
    public modalCtrl: ModalController,
    public navSvc: NavigationService,
    public navParams: NavParams,
    public stateData: StateManagementService,
    public appCtrl: App,
    public dataService: OrderDataService,
    public events: Events,
    private fb: FormBuilder,
    private orderManagementDataService: OrderManagementDataService,
    private toastCtrl: ToastController,
    private popoverCtrl: PopoverController,
    private documentSvc: DocManagementDataService,
    public loadingCtrl: LoadingController,
    private elementRef: ElementRef,
    private reportExecutionService: ReportExecutionService,
    private careTeamsService: CareTeamsService,
    private alertCtrl: AlertController,
    private resourceManagementService: ResourceManagementService,
    private authSvc: AuthService,
    private utcDateTimeZonePipe: UtcDateTimeZonePipe,
    private configurationService: ConfigurationService,
    private location: Location,
    private nciIndicatorsService: NciIndicatorsService
  ) {
    super(stateData);
    this.searchForm = this.fb.group({
      fromDate: this.fb.control(""),
      toDate: this.fb.control(""),
      type: this.fb.control(null),
      signed: this.fb.control(null),
      team: this.fb.control(null),
      orderingProvider: this.fb.control(null),
      hospiceCoverage: this.fb.control(null),
    });
    this.newPatientProfileBaseUrl =
      this.configurationService.getConfig().clinicalUrl;
    this.patientProfileUrl = this.configurationService.getConfig().logoutUrl;
  }

  ngAfterViewInit() {
    this.searchIconEl = this.elementRef.nativeElement.querySelector(
      ".searchbar-search-icon"
    );
    if (this.searchIconEl) {
      this.boundOnSearchIconClick = this.getItemsBySearchFilter.bind(this);
      this.searchIconEl.addEventListener("click", this.boundOnSearchIconClick);
    }
  }

  sendToProfilePage(patientChartId) {
    this.navSvc.goToPatientProfile(patientChartId);
  }

  loadData(): void {
    this.orders = [];
    // TRD-201 / REQ-006: clear stale indicators before refetch — same lifecycle as `this.orders`.
    this.nciIndicators = new Map<number, NciIndicator>();
    this.createSearchRequestBody();
    // If you don't have the slider set to Show Signed Orders and your filter is not null or no, then you have mutually exclusive filters.
    // Return no orders.
    if (
      !this.signedOrdersEnabled &&
      !(
        !this.signedStatus ||
        this.signedStatus == OrderSignatureRequest.SignedStatusEnum.No
      )
    ) {
      // CO-3361: also reset the pager. Clearing `orders` alone left the pager
      // advertising pages of a result set that is no longer on screen, so
      // clicking one issued a search for page N of nothing.
      this.totalPages = 0;
      return;
    }

    this.increaseSpinnerCount("Loading orders ...");

    this.orderManagementDataService
      .searchOrders(this.orderSearchDetails)
      .finally(() => this.decreaseSpinnerCount())
      .subscribe((OpenSearchResponse) => {
        if (OpenSearchResponse && OpenSearchResponse.hits) {
          this.orders = OpenSearchResponse.hits;
          this.orders = this.orders.map((s) => {
            s = {
              ...s,
              showMorePNotes: false,
              showMoreHReason: false,
              showMoreBodySystems: false,
              isOrderRejected: this.setIsOrderRejected(s),
              reasonForOrderRejection:
                this.setRejectedReasonIfOrderRejectedAndNoReasonProvided(s),
              isOrderDiscontinued: this.setIsOrderDiscontinued(s),
            };
            if (s.isOrderRejected) {
              s.reasonForOrderRejection =
                this.setRejectedReasonIfOrderRejectedAndNoReasonProvided(s);
            }
            this.setDiscontinuedActionReasonIfOrderDiscontinuedAndNoReasonProvided(
              s
            );
            return s;
          });
          this.totalPages = Math.ceil(
            OpenSearchResponse.totalHits / this.pageSize
          );
        } else {
          this.orders = [];
          this.totalPages = 0;
        }
        // TRD-201 / REQ-006 (TRD §4.4): after orders settle, fetch indicators for the
        // current grid page. Best-effort UX — see NciIndicatorsService for fail-open
        // semantics. Runs after the orders are populated so the icon binds via
        // `getNciIndicator(order)` in the template.
        this.loadNciIndicators();
      }, OrderErrorResponse.handleError(this.toastCtrl, "Error loading orders."));
  }

  /**
   * TRD-201 / REQ-006: Fetch NCI indicators for the currently-loaded orders and rebuild
   * `this.nciIndicators` (Map keyed by orderId) for template lookup.
   *
   * OMF order responses carry `orderId`, not `id`. Coerce via Number(o.orderId) to match
   * the Map<number, NciIndicator> key type. The earlier `(o as any).id` cast always
   * yielded undefined, so `orderIds` stayed empty and NciIndicatorsService.getIndicators
   * short-circuited to of([]) — the HTTP call never fired and no "N" icon ever rendered.
   *
   * Only currently-non-covered orders (`hospicePays === false`) contribute to the request:
   * the backend NCI Indicators endpoint scopes its query to `non_covered_item_order_link`
   * junction rows, which are written only at addendum-generation time and only for
   * non-covered orders. Mirrors the Order Entry implementation (commit aacacef101).
   */
  private loadNciIndicators(): void {
    if (!this.tempNciToggle) {
      return;
    }
    const orderIds: number[] = (this.orders || [])
      .filter((o) => o.hospicePays === false)
      .map((o) => Number(o.orderId))
      .filter((id) => !Number.isNaN(id));
    this.nciIndicatorsService.getIndicators(orderIds).subscribe((indicators) => {
      const map = new Map<number, NciIndicator>();
      for (const indicator of indicators || []) {
        map.set(indicator.orderId, indicator);
      }
      this.nciIndicators = map;
    });
  }

  /**
   * Template helper for the "N" non-covered-item indicator on the orders grid.
   *
   * Returns the NciIndicator for an order, or undefined if none. OMF order responses
   * carry `orderId`, not `id`, so the lookup must use `order.orderId`; coerce to a
   * number to match the Map<number, NciIndicator> key set by `loadNciIndicators`
   * (`map.set(indicator.orderId, ...)` — backend returns orderId as a number).
   *
   * Exists as a component method because Angular 5 / Ionic 3 template expressions
   * do not expose `Number` as a global, so `Number(order.orderId)` cannot be invoked
   * inline in the template. Centralizing the coercion here also keeps the lookup
   * semantics identical between the `*ngIf` predicate and the date-binding read.
   */
  getNciIndicator(order: any): NciIndicator | undefined {
    if (!this.nciIndicators || !order) {
      return undefined;
    }
    const key = Number(order.orderId);
    if (Number.isNaN(key)) {
      return undefined;
    }
    return this.nciIndicators.get(key);
  }

  setDiscontinuedActionReasonIfOrderDiscontinuedAndNoReasonProvided(
    order: StatefulOrderResponse
  ) {
    if (order.discontinuedAction) {
      if (!order.discontinuedAction.reason) {
        order.discontinuedAction.reason = "No Details Entered (via User)";
      }
    }
  }

  setRejectedReasonIfOrderRejectedAndNoReasonProvided(
    order: StatefulOrderResponse
  ): string {
    const NO_REASON_MESSAGE = "No Details Entered (via User)";
    let reason: string;
    if (order.signatureRequests?.created?.signature) {
      const signature = order.signatureRequests.created.signature;
      if (signature.actionType === Action.ActionTypeEnum.Rejected) {
        if (Action.hasReason(signature)) {
          reason = signature.reason;
        } else {
          reason = NO_REASON_MESSAGE;
        }
      }
    }
    return reason;
  }

  ionViewWillEnter(): void {
    this.getOrderTypeConfigurations();
    this.getUserDisplayName();
  }

  getOrderTypeConfigurations(): void {
    this.orderManagementDataService
      .getOrderTypes()
      .subscribe((orderTypeConfigurations) => {
        this.orderTypeConfigurations = orderTypeConfigurations;
        this.setOrderTypeList(this.orderTypeConfigurations);
      }, OrderErrorResponse.handleError(this.toastCtrl, "Could not get order type configurations."));
  }

  trimString(string, length) {
    return string.length > length
      ? string.substring(0, length) + "..."
      : string;
  }

  onArrowClicked(order: OrderResponse): void {
    const orderTypeConfiguration = this.orderTypeConfigurations.find(
      (orderTypeConfiguration) =>
        orderTypeConfiguration.orderType == order.orderType
    );
    this.historyDetails = getHistoryDetailSummary(
      order,
      orderTypeConfiguration
    );
    this.orders.forEach((x) => {
      if (x === order) {
        x.showDetails = !x.showDetails;
      } else {
        x.showDetails = false;
      }
      return x;
    });
  }

  refresh() {
    this.loadData();
  }

  ngOnInit(): void {
    this.tempNciToggleSubscription = this.stateData.tempNciToggle.subscribe(
      (value) => {
        this.tempNciToggle = value;
      }
    );
    this.loadData();
    this.loadTeams();
    this.initProviderSearch();
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    if (this.tempNciToggleSubscription) {
      this.tempNciToggleSubscription.unsubscribe();
    }
    if (this.searchIconEl && this.boundOnSearchIconClick) {
      this.searchIconEl.removeEventListener(
        "click",
        this.boundOnSearchIconClick
      );
    }
  }

  getUserDisplayName() {
    this.resourceManagementService
      .getProviderByExternalId(this.authSvc.getUser().userId)
      .subscribe((provider) => {
        this.userDisplayName = provider.findDisplayName(new Date());
      });
  }

  exitOrderManagment() {
    this.location.back();
  }

  getDisplayName(externalId: string, date: Date = new Date()) {
    if (this.professionalProviders) {
      const professionalProvider = this.professionalProviders.get(
        Number(externalId)
      );
      if (!professionalProvider) {
        return "";
      }
      return professionalProvider.findDisplayName(date);
    }

    return "Loading...";
  }

  presentEditViewPopover(
    $event,
    order: OrderResponse,
    signatureRequest: Action.SignatureRequestEnum
  ) {
    if (!this.userDisplayName) {
      let toast = this.toastCtrl.create({
        message: `Unable to retrieve user info from Resources, please confirm user has been entered.`,
        duration: 3000,
        position: "below",
        cssClass: "error",
      });
      toast.present();
      return;
    }
    let popover = this.popoverCtrl.create(
      EditViewPopoverPage,
      {
        // CO-3361 r2: `hideAllOrderManagment: true` + `isPatientChart: true`
        // selects EditViewPopover's Order-Management block, which offers exactly
        // two actions — Upload Signed Order and Add an Operational Note. There
        // is no delete on this screen. `onlyDelete: true`, `deleteLabel:
        // "Remove"` and `disabledIcon: true` used to be passed here and were
        // read by NO block that can render under these flags: every delete
        // block requires `!hideAllOrderManagment`. They advertised a Remove
        // capability that does not exist, so they are gone.
        hideAllOrderManagment: true,
        hideOrderManagment: false,
        isPatientChart: true,
        showButtonsAsList: false,
        showMedicationEdit: false,
        orderId: order.orderId,
        orderPatientChartId: order.patientChartId,
        order: order,
        userDisplayName: this.userDisplayName,
        signatureRequest: signatureRequest,
      },
      {
        cssClass: "more-options-list-popover",
      }
    );
    popover.present({
      ev: $event,
    });
    popover.onDidDismiss((mode) => {
      if (mode.updateFile$) {
        this.increaseSpinnerCount("Adding files to order...");
        (mode.updateFile$ as Observable<OrderResponse>)
          .finally(() => this.decreaseSpinnerCount())
          .subscribe((_data) => {
            this.loadData();
          }, OrderErrorResponse.handleError(this.toastCtrl, "Error adding files to order."));
      } else {
        this.loadData();
      }
    });
  }

  showDocuments(
    $event,
    order: OrderResponse,
    signatureRequest: Action.SignatureRequestEnum
  ) {
    const orderSignatureRequest =
      signatureRequest === "created"
        ? order.signatureRequests.created
        : order.signatureRequests.discontinued;

    this.increaseSpinnerCount("Loading File(s)...");
    of(...orderSignatureRequest.documentIds)
      .pipe(
        mergeMap((id) =>
          this.documentSvc.getDocumentToView(id).pipe(
            catchError((err) => {
              this.presentErrorToast(
                err,
                `Document failed to load: ${err.message}`
              );
              return of(undefined);
            })
          )
        ),
        filter<ArrayBuffer | undefined>((document) => document !== undefined)
      )
      .toArray()
      .subscribe((documents) => {
        documents.forEach((data) => {
          const file = new Blob([data], { type: "application/pdf" });
          const fileURL = URL.createObjectURL(file);
          window.open(fileURL, "_blank");
        });
        this.decreaseSpinnerCount();
      });
  }

  increaseSpinnerCount(message: string) {
    this.spinnerCounter++;
    if (this.spinnerCounter && this.spinner == null) {
      this.spinner = this.loadingCtrl.create({
        content: message,
      });
      this.spinner.present();
    }
  }

  decreaseSpinnerCount() {
    this.spinnerCounter--;
    if (this.spinnerCounter <= 0 && this.spinner) {
      this.spinner.dismiss();
      this.spinner = null;
    }
  }

  onToggleDiscontinuedOrders(toggle: Toggle) {
    this.discontinuedOrdersEnabled = toggle.value;
    this.loadData();
  }

  onToggleSignedOrders(toggle: Toggle) {
    this.signedOrdersEnabled = toggle.value;
    this.loadData();
  }

  presentErrorToast(err: HttpErrorResponse, message: string) {
    message += " ";
    if (err.error instanceof Error) {
      message += "Client error.";
    } else {
      message += " ";
      if (err.status == 401) {
        message += "User not authorized.";
      } else if (err.status >= 500) {
        // Do not modify message
      } else {
        message += "Communication error.";
      }
    }

    let toast = this.toastCtrl.create({
      message: message,
      duration: 6000,
      position: "below",
      cssClass: "error",
    });

    toast.present();
  }

  createSearchRequestBody() {
    let details: OrderSearchRequest = {};
    details.orderingProviderId = this.orderingProviderId;
    details.orderType = this.orderTypes;
    details.signedStatus = this.signedOrdersEnabled
      ? this.signedStatus
      : OrderSignatureRequest.SignedStatusEnum.No;
    details.queryString = this.search === "" ? undefined : this.search;
    details.dateRange = this.getDateRange();
    details.from = this.currentPage * this.pageSize;
    details.size = this.pageSize;
    details.sortBy = {
      name: this.sortColumn,
      value: this.sortDirection,
    };
    details.teamId = this.team;
    details.includeDiscontinued = this.discontinuedOrdersEnabled;
    details.hospicePays = this.hospiceCoverage;
    this.orderSearchDetails = details;
  }

  getDateRange(): OrderSearchDateRange | undefined {
    // CO-3361: `clearFilters()` resets the dates to `undefined`, not `null`, so
    // a strict `=== null` check skipped this early return and the page sent
    // `dateRange: {}` — no bounds, not even a `field` — after every Clear
    // Filters. `== null` covers both.
    if (this.fromDate == null && this.toDate == null) {
      return undefined;
    } else {
      let dateRange: OrderSearchDateRange = {};
      if (this.fromDate != null) {
        dateRange.fromDate = this.formatDate(this.fromDate);
        dateRange.field = DATE_RANGE_COLUMN;
      }

      if (this.toDate != null) {
        dateRange.toDate = this.formatDate(this.toDate);
        dateRange.field = DATE_RANGE_COLUMN;
      }
      return dateRange;
    }
  }

  formatDate(date) {
    if (date !== null) {
      return moment(date).format("YYYY-MM-DD");
    } else {
      return date;
    }
  }

  sortBy(selectedColumn: string) {
    if (this.sortColumn != selectedColumn) {
      this.sortColumn = selectedColumn;
      this.sortDirection = "desc";
    } else {
      this.sortDirection = this.sortDirection === "asc" ? "desc" : "asc";
    }
    this.currentPage = 0;
    this.loadData();
  }

  getCaretState(column: string) {
    if (this.sortColumn === column && this.sortDirection === "asc") {
      return "ios-arrow-up";
    }
    return "ios-arrow-down";
  }

  /**
   * CO-3361: the value for a column header's `aria-sort`. The grid sorts
   * server-side and only one column is ever active, so every other header
   * reports "none". Exists as a method because repeating the ternary inline on
   * ten headers is where drift starts.
   */
  getAriaSort(column: string): string {
    if (this.sortColumn !== column) {
      return "none";
    }
    return this.sortDirection === "asc" ? "ascending" : "descending";
  }

  getOrderTypeDisplayName(orderTypeSubtype: OrderTypeSubtype) {
    return getOrderTypeDisplayName(
      orderTypeSubtype,
      this.orderTypeConfigurations
    );
  }

  getDiscontinuedProviderDisplayName(order: StatefulOrderResponse) {
    return getDiscontinuedProviderDisplayName(order);
  }

  onPageChange(selectedPage: number) {
    this.currentPage = selectedPage;
    this.loadData();
  }

  clearFilters() {
    this.fromDate = undefined;
    this.toDate = undefined;
    this.orderTypes = undefined;
    this.orderTypeDropdown.clearModel();
    this.signedDropdown.clearModel();
    this.currentPage = 0;
    this.team = undefined;
    this.teamDropdown.clearModel();
    this.orderingProviderId = null;
    this.orderingProvider.clearModel();
    this.hospiceCoverage = null;
    this.hospiceCoverageDropdown?.clearModel();
    this.loadData();
  }

  applyFiltersOS() {
    this.currentPage = 0;
    this.loadData();
  }

  public setOrderTypeList(
    orderTypeConfigurations: Array<OrderTypeConfiguration>
  ): void {
    this.orderTypeDropdownItems = orderTypeConfigurations
      .filter(
        (orderTypeConfiguration) =>
          orderTypeConfiguration.orderType &&
          orderTypeConfiguration.orderTypeDisplayName &&
          (orderTypeConfiguration.isDisabled == false ||
            orderTypeConfiguration.isDisabled === undefined)
      )
      .sort(
        (config1, config2) =>
          (config1.orderTypeSort ? config1.orderTypeSort : Infinity) -
          (config2.orderTypeSort ? config2.orderTypeSort : Infinity)
      )
      .map((orderTypeConfiguration) => {
        return {
          label: orderTypeConfiguration.orderTypeDisplayName,
          value: orderTypeConfiguration.orderType,
        };
      });
  }

  onOrderTypeChange(event: DropdownItem) {
    if (event) {
      this.orderTypes = event.value;
    } else {
      this.orderTypes = null;
    }
  }

  getItemsBySearchFilter() {
    this.currentPage = 0;
    this.loadData();
  }

  clearSearchBar() {
    this.search = null;
    this.currentPage = 0;
    this.loadData();
  }

  openOptionsPopover(ev) {
    // Start with print options
    let options = [...this.printOptions];

    // Add fax options only if WestFax or WorldViewFax toggle is enabled
    const isWestFaxEnabled = this.stateData.isWestFaxToggleOn.getValue();
    const isWorldViewFaxEnabled =
      this.stateData.isWorldViewFaxToggleOn.getValue();
    if (isWestFaxEnabled || isWorldViewFaxEnabled) {
      options = [...options, ...this.faxOptions];
    }

    // Add external export option
    options = [...options, ...this.externalExportOptions];

    const popover = this.popoverCtrl.create(
      "OMPrintMenuPage",
      { options },
      { cssClass: "multiple-select-custom-popover" }
    );

    popover.onDidDismiss((option) => {
      if (option && option.key == "print") {
        this.handlePrint();
      } else if (option && option.key == "fax") {
        this.handleFax();
      } else if (option && option.key == "externalExport") {
        this.handleExternalExport();
      }
    });

    popover.present({ ev });
  }

  handlePrint() {
    // Print selected orders only
    this.printSelectedOrders();
  }

  handleFax() {
    // Fax selected orders only
    // Check if we have orders to fax before opening modal
    const orderIds = this.checkedOrders;

    if (orderIds.length == 0) {
      const toast = this.toastCtrl.create({
        message: NO_ORDERS_TO_FAX,
        showCloseButton: true,
        position: "below",
        cssClass: "error",
      });
      toast.present();
      return;
    }

    // Create callback function to handle API call
    const sendFaxCallback = (faxData: any): Promise<boolean> => {
      return new Promise((resolve) => {
        // Remove formState from the request body
        const { formState, ...cleanFaxData } = faxData;

        let requestBody = this.buildFaxPayload(orderIds.toString());
        requestBody = { ...requestBody, ...cleanFaxData };

        if (!requestBody.senderPhone || requestBody.senderPhone === "") {
          const toast = this.toastCtrl.create({
            message: "Could not send Fax, please contact your administrator",
            showCloseButton: true,
            position: "below",
            cssClass: "error",
          });
          toast.present();
          resolve(false); // Don't close modal if validation fails
          return;
        }

        // Show loading spinner while sending fax
        this.increaseSpinnerCount("Sending fax...");

        this.orderManagementDataService.faxOrders(requestBody).subscribe(
          () => {
            this.decreaseSpinnerCount();
            // Handle fax sent successfully
            let toast = this.toastCtrl.create({
              message: "Fax sent successfully",
              duration: 3000,
              position: "bottom",
              cssClass: "success",
            });
            toast.present();
            resolve(true); // Close modal on success
          },
          () => {
            this.decreaseSpinnerCount();
            const toast = this.toastCtrl.create({
              message: "Error sending fax, please try again",
              showCloseButton: true,
              position: "below",
              cssClass: "error",
            });
            toast.present();
            resolve(false); // Don't close modal on error
          }
        );
      });
    };

    const faxModal = this.modalCtrl.create(
      FaxModalComponent,
      {
        sendFaxCallback: sendFaxCallback,
      },
      {
        cssClass: "popup-medium",
        enableBackdropDismiss: false,
      }
    );

    faxModal.present();
  }

  buildFaxPayload(listOrderId: string) {
    let payload = {
      patientChartId: "",
      orderId: listOrderId,
      companyName: this.authSvc.getUser().tenantName,
      senderPhone: this.authSvc.getUser().tenantPhone,
      orderStatus: "All",
    };
    return payload;
  }

  printSelectedOrders() {
    if (this.checkedOrders.length == 0) {
      const toast = this.toastCtrl.create({
        message: NO_ORDERS_TO_PRINT_SELECTED,
        showCloseButton: true,
        position: "below",
        cssClass: "error",
      });
      toast.present();
      return;
    }
    if (this.checkedOrders.length > 20) {
      this.warningPopupSelected(this.printSelectedMsg());
    } else {
      this.downloadOrderReport();
    }
  }
  showAllPatientOrdersDashboard() {
    this.quicksightDashboardName = ALL_PATIENT_ORDERS_QUICKSIGHT_DASHBOARD_NAME;
    this.quicksightDashboardParameters = {
      tenant_id: this.authSvc.getUser().tenantId.toString(),
    };
    this.showQuicksightDashboard = true;
  }

  closeQuicksightDashboard() {
    this.showQuicksightDashboard = false;
  }

  printSelectedMsg() {
    return `You have chosen to print a total of (${this.checkedOrders.length}) orders. Please note that you can only print 20 orders at a time. Therefore, only the first 20 orders will be printed.`;
  }

  warningPopupSelected(message: string) {
    const alert = this.alertCtrl.create({
      enableBackdropDismiss: false,
      title: "Print",
      message: message,
      buttons: [
        {
          text: "CANCEL",
        },
        {
          text: "PROCEED",
          handler: () => {
            this.downloadOrderReport();
          },
        },
      ],
    });
    alert.present();
  }

  downloadOrderReport() {
    const orderIds = this.checkedOrders;
    if (orderIds.length == 0) {
      const toast = this.toastCtrl.create({
        message: NO_ORDERS_TO_PRINT_SELECTED,
        showCloseButton: true,
        position: "below",
        cssClass: "error",
      });
      toast.present();
      return;
    }
    let valueSelected = orderIds.slice(0, 20).toString();
    this.reportExecutionService.downloadReport(
      "bulk_order_report",
      this.buildReportPayload(valueSelected),
      this.reportExecutionService.buildReportFilename(
        "bulk_order_report",
        "pdf"
      )
    );
  }

  buildReportPayload(listOrderId: string) {
    const orgId = this.authSvc.getUser().tenantId.toString();
    return {
      pPatientChartID: "",
      pOrgID: orgId,
      pOrderID: listOrderId,
      pOrderStatus: "All",
    };
  }

  onTeamChange(event: DropdownItem) {
    if (event) {
      this.team = parseInt(event.value);
    } else {
      this.team = null;
    }
  }

  loadTeams() {
    this.careTeamsService
      .getAllTeamsByTenant()
      .subscribe((data: CareTeam[]) => {
        this.teamDropdownItems = data.map((item) => {
          return {
            label: item.name,
            value: item.id,
          };
        });
      });
  }

  selectAllOrders(event: Checkbox) {
    for (const order of this.orders) {
      this.onCheckOrder({ value: event.checked }, order);
    }
  }

  onCheckOrder(event: { value: boolean }, order: StatefulOrderResponse) {
    this.checkedOrdersMap[order.orderId] = event.value ? order : undefined;
    this.checkedOrders = Object.entries(this.checkedOrdersMap)
      .filter(([_, checked]) => checked)
      .map(([orderId, _]) => orderId);
  }

  // Clear button pressed for checked orders section
  onClearCheckboxes() {
    for (const key of Object.keys(this.checkedOrdersMap)) {
      this.checkedOrdersMap[key] = undefined;
    }
    this.checkedOrders = [];
    this.selectAllOrdersCheckbox = false;
  }

  isOrderChecked(order: StatefulOrderResponse): boolean {
    // order -> true, undefined -> false
    return !!this.checkedOrdersMap[order.orderId];
  }

  // Close on open - this prevents the user from using the checked orders dropdown
  onOpenCheckedOrders() {
    this.checkedOrdersDropdown.close();
  }

  // Clear button pressed for checked order
  onUncheckOrder(event: NgSelectOption) {
    this.onCheckOrder({ value: false }, {
      orderId: event.value,
    } as StatefulOrderResponse);
  }

  fromDateHandler() {
    let toDate = this.formatDate(this.toDate);
    let fromDate = this.formatDate(this.fromDate);
    if (moment(toDate).isBefore(fromDate)) {
      this.fromDate = null;
      this.searchForm.get("fromDate").setValue("");
      this.toastError("From Date must be less than To Date.");
    }
  }

  toDateHandler() {
    let toDate = this.formatDate(this.toDate);
    let fromDate = this.formatDate(this.fromDate);
    if (moment(fromDate).isAfter(toDate)) {
      this.toDate = null;
      this.searchForm.get("toDate").setValue("");
      this.toastError("To Date must be greater than From Date.");
    }
  }

  toastError(message: string) {
    let toast = this.toastCtrl.create({
      message: message,
      duration: 3000,
      position: "below",
      cssClass: "error",
    });
    toast.present().then();
  }

  onChangeDatepicker(event: any, control: string) {
    if (event) {
      let value = event.target.value;
      let valueFormatted = moment(value, "MM/DD/YYYY", true);
      if (valueFormatted.isValid()) {
        switch (control) {
          case "fromDate":
            this.fromDate = new Date(valueFormatted);
            this.searchForm.get("fromDate").setValue(this.fromDate);
            this.fromDateHandler();
            break;
          case "toDate":
            this.toDate = new Date(valueFormatted);
            this.searchForm.get("toDate").setValue(this.toDate);
            this.toDateHandler();
            break;
        }
        this.error = false;
      } else {
        this.error = true;
      }
    }
  }

  // Hackish fix to show keyboard on IOS / Safari
  onClick(event: PointerEvent) {
    this.hiddenInput.setFocus();
    const element = event.target as HTMLElement;
    // Clicked on input
    if (element.constructor.name === "HTMLInputElement") {
      element.focus();
    }
    // Clicked on a parent of input
    const input = element.querySelector("input");
    if (input) {
      input.focus();
    }
  }
  formatPreviousValueDisplay(value: string, changeMadeTo: string) {
    const allowedKeys = ["address1", "address2", "city", "state", "zip", "ZIP"];
    if (changeMadeTo === "Patient Location") {
      var data = JSON.parse(value);
      var formattedData = allowedKeys
        .filter((key) => data[key] !== null && data[key] !== undefined)
        .map((key) => data[key])
        .filter((value) => value !== "-")
        .join(", ");
      return formattedData;
    }
    return value;
  }

  getDiscontinueDateDisplay(order: StatefulOrderResponse) {
    let timezone = order.discontinuedAction
      ? order.discontinuedAction.timezone
      : undefined;
    return order.canceledAction
      ? "Canceled"
      : this.utcDateTimeZonePipe.transform(
          order.discontinueDate,
          timezone,
          "dateOnly"
        );
  }

  initProviderSearch() {
    this.providerDropdownItems$ = this.providerQuery$.pipe(
      distinctUntilChanged(),
      filter((query) => !query || ![1, 2].includes(query.length)), // do nothing if 1 or 2 characters
      switchMap((query: string | null) => {
        // null -> clear, '' -> field cleared by backspace
        if (!query || query.length === 0) {
          return of([]);
        }
        return this.resourceManagementService
          .getCompanyResourceSummaries(
            0,
            100,
            query ? query : undefined,
            new SortOptions("last_name", "asc"),
            CompanyResourceType.PROVIDER
          )
          .pipe(
            mergeMap((providerList) =>
              of(
                providerList.content.map((provider) => {
                  return {
                    label: provider.displayName,
                    value: `${provider.externalId}`,
                  };
                })
              )
            ),
            catchError(() => of([]))
          );
      })
    );
  }

  /**
   * CO-3361: ng-select emits (change) with `undefined` whenever the selection is
   * cleared — `changeEvent.emit(selectedItems[0] && selectedItems[0].value)`.
   * Dereferencing `event.value` unguarded threw a TypeError, and because
   * `clearFilters()` calls `orderingProvider.clearModel()` the throw aborted the
   * rest of Clear Filters, including the `loadData()` that refreshes the grid.
   * The sibling handlers (onOrderTypeChange / onTeamChange) guard the same way.
   */
  onProviderSelected(event: DropdownItem) {
    if (event) {
      this.orderingProviderId = +event.value;
    } else {
      this.orderingProviderId = null;
    }
  }

  orderIsDeclined(order: OrderResponse) {
    return Order.isVisitFrequencyDeclined(order);
  }

  setIsOrderRejected(order) {
    return (
      order.signatureRequests?.created?.signedStatus ===
      OrderSignatureRequest.SignedStatusEnum.Rejected
    );
  }

  setIsOrderDiscontinued(order) {
    return order.discontinuedAction != null;
  }

  handleExternalExport() {
    // Check if we have orders to export
    if (this.checkedOrders.length == 0) {
      const toast = this.toastCtrl.create({
        message: "No orders selected to export",
        showCloseButton: true,
        position: "below",
        cssClass: "error",
      });
      toast.present();
      return;
    }

    const selectedOrders = Object.values(this.checkedOrdersMap).filter(
      (order) => order
    );

    // Open the External Export modal
    const exportModal = this.modalCtrl.create(
      ExternalExportOrderModalComponent,
      {
        selectedOrders: selectedOrders,
        orderTypeConfigurations: this.orderTypeConfigurations,
      },
      {
        cssClass: "popup-medium",
        enableBackdropDismiss: false,
      }
    );

    exportModal.onDidDismiss((result) => {
      if (result && result.success) {
        // Download the order report (same as Print action)
        this.downloadOrderReport();
      }
    });

    exportModal.present();
  }
}



// WEBPACK FOOTER //
// ./src/pages/order-managment/list-orders/list-orders.ts